import {
  type CSSProperties,
} from "react";
import {
  formatEvaluationAmount,
} from "../../model";
import { OfferDocumentsInline } from "../../presentation/shared/RequestDocuments";
import {
  formatScoreMetric,
  offerScoreExplanation,
} from "./approval-presentation";
import type { ApprovalRowContext } from "./approval-row-model";

export function ApprovalOfferComparison({ context }: { context: ApprovalRowContext ;}) {
  const {
    request,
    user,
    workspaceByRequest,
    selectGmFinalOffer,
    isActionable,
    evaluation,
    systemOffer,
    selectedOffer,
    managerApproval,
    managerSelectedOffer,
    rankedOffers,
    bestUnitPrice,
    bestDeliveryDays,
    bestPaymentDays,
    isCompleted,
    displayedOffer,
    gmFinalOfferId,
  } = context;
  if (!evaluation || !displayedOffer) return null;
  return (<>
    <section
      className="approval-offer-section"
      aria-label="Teklif karşılaştırması"
    >
      <header>
        <div>
          <strong>
            Teklifleri karşılaştır
          </strong>
          <span>
            {evaluation.offers.length} geçerli
          </span>
        </div>
        <small>
          Avantajlar sütun bazında işaretli
        </small>
      </header>
      <div
        className="approval-offer-table-head"
        aria-hidden="true"
      >
        <span>Tedarikçi / teklif</span>
        <span>Birim fiyat</span>
        <span>Toplam</span>
        <span>Teslimat</span>
        <span>Vade</span>
        <span>Puan</span>
      </div>
      <div className="approval-offer-comparison">
        {rankedOffers.map((offer) => {
          const isManagerSelection =
            Boolean(managerApproval) &&
            offer.id ===
            managerSelectedOffer?.id;
          const isSpecialistSelection =
            offer.id === selectedOffer?.id;
          const isSystemRecommendation =
            offer.id === systemOffer?.id;
          const isFinalSelection =
            offer.id ===
            (user.role === "GENEL_MUDUR" &&
              isActionable
              ? gmFinalOfferId
              : isCompleted
                ? displayedOffer.id
                : "");
          const offerAttachments =
            workspaceByRequest[
              request.id
            ]?.attachments.filter(
              (attachment) =>
                attachment.offerNo ===
                offer.offerNo ||
                (!attachment.offerNo &&
                  evaluation.offers.length ===
                  1),
            ) ?? [];
          const content = (
            <>
              <span>
                <strong>
                  {offer.rank !== null ? (
                    <b className="approval-offer-rank">
                      {offer.rank}
                    </b>
                  ) : null}
                  {offer.supplier}
                </strong>
                <span className="approval-offer-meta">
                  <small>
                    {offer.offerNo}
                  </small>
                  {isSystemRecommendation ? (
                    <em className="approval-offer-status is-system">
                      Sistem önerisi
                    </em>
                  ) : null}
                  {isFinalSelection ? (
                    <em className="approval-offer-status is-selection">
                      {isCompleted
                        ? "Nihai seçim"
                        : "Seçili teklif"}
                    </em>
                  ) : isManagerSelection ? (
                    <em className="approval-offer-status is-selection">
                      Müdür önerisi
                    </em>
                  ) : isSpecialistSelection &&
                    !isSystemRecommendation ? (
                    <em className="approval-offer-status is-selection">
                      Uzman tercihi
                    </em>
                  ) : null}
                </span>
                <OfferDocumentsInline
                  attachments={
                    offerAttachments
                  }
                />
              </span>
              <span
                className={
                  offer.unitPrice !== null &&
                    offer.unitPrice ===
                    bestUnitPrice
                    ? "is-best-metric"
                    : undefined
                }
              >
                <small className="approval-offer-cell-label">
                  Birim fiyat
                </small>
                <strong>
                  {formatEvaluationAmount(
                    offer.unitPrice,
                    offer.currency ??
                    evaluation.comparisonCurrency,
                  )}{" "}
                  /{" "}
                  {offer.unit ??
                    request.unit ??
                    "birim"}
                </strong>
                {offer.unitPrice !== null &&
                  offer.unitPrice ===
                  bestUnitPrice ? (
                  <small className="approval-offer-best">
                    En düşük
                  </small>
                ) : null}
              </span>
              <span>
                <small className="approval-offer-cell-label">
                  Toplam
                </small>
                <strong>
                  {formatEvaluationAmount(
                    offer.comparableTotal ??
                    offer.enteredTotal ??
                    offer.amount,
                    evaluation.comparisonCurrency,
                  )}
                </strong>
              </span>
              <span
                className={
                  offer.deliveryDays !==
                    null &&
                    offer.deliveryDays ===
                    bestDeliveryDays
                    ? "is-best-metric"
                    : undefined
                }
              >
                <small className="approval-offer-cell-label">
                  Teslimat
                </small>
                <strong>
                  {offer.deliveryDays !== null
                    ? `${offer.deliveryDays} gün`
                    : "—"}
                </strong>
                {offer.deliveryDays !==
                  null &&
                  offer.deliveryDays ===
                  bestDeliveryDays ? (
                  <small className="approval-offer-best">
                    En hızlı
                  </small>
                ) : null}
              </span>
              <span
                className={
                  offer.paymentDays !==
                    null &&
                    offer.paymentDays ===
                    bestPaymentDays
                    ? "is-best-metric"
                    : undefined
                }
              >
                <small className="approval-offer-cell-label">
                  Vade
                </small>
                <strong>
                  {offer.paymentDays !== null
                    ? `${offer.paymentDays} gün`
                    : "—"}
                </strong>
                {offer.paymentDays !== null &&
                  offer.paymentDays ===
                  bestPaymentDays ? (
                  <small className="approval-offer-best">
                    En uzun vade
                  </small>
                ) : null}
              </span>
              <span>
                <small className="approval-offer-cell-label">
                  Puan
                </small>
                <strong
                  className="approval-offer-score"
                  data-tooltip={offerScoreExplanation(
                    offer,
                    evaluation,
                  )}
                  aria-label={`Puan ${formatScoreMetric(offer.totalScore ?? 0)}. ${offerScoreExplanation(offer, evaluation).replaceAll("\n", ". ")}`}
                >
                  <span
                    aria-hidden="true"
                    className="approval-offer-score-track"
                    style={
                      {
                        "--offer-score": `${Math.max(
                          0,
                          Math.min(
                            100,
                            offer.totalScore ??
                            0,
                          ),
                        )}%`,
                      } as CSSProperties
                    }
                  />
                  {offer.totalScore !== null
                    ? Math.round(
                      offer.totalScore,
                    )
                    : "—"}
                  {offer.totalScore !==
                    null ? (
                    <small>/100</small>
                  ) : null}
                </strong>
              </span>
            </>
          );
          return user.role ===
            "GENEL_MUDUR" && isActionable ? (
            <div
              key={offer.id}
              role="button"
              tabIndex={0}
              className={
                isFinalSelection
                  ? "is-selected"
                  : ""
              }
              aria-pressed={isFinalSelection}
              onClick={() =>
                selectGmFinalOffer(
                  request.id,
                  offer.id,
                )
              }
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" ||
                  event.key === " "
                ) {
                  event.preventDefault();
                  selectGmFinalOffer(
                    request.id,
                    offer.id,
                  );
                }
              }}
            >
              {content}
            </div>
          ) : (
            <div
              key={offer.id}
              className={
                isManagerSelection
                  ? "is-selected"
                  : isSystemRecommendation
                    ? "is-recommended"
                    : ""
              }
            >
              {content}
            </div>
          );
        })}
      </div>
    </section>
  </>);
}
